/**
 * Política de áudio da sala: desbloquear playback no gesto do usuário
 * e republicar o mic após reconnect / Krisp — sem o “sair e entrar”.
 */

export function shouldShowAudioUnlockOverlay(canPlaybackAudio: boolean): boolean {
  return canPlaybackAudio === false;
}

export function shouldAttemptStartAudio(canPlaybackAudio: boolean): boolean {
  return canPlaybackAudio === false;
}

export function mediaToRestoreAfterReconnect(settings: {
  audioEnabled?: boolean;
  videoEnabled?: boolean;
}): { restoreMic: boolean; restoreCamera: boolean } {
  return {
    restoreMic: settings.audioEnabled === true,
    restoreCamera: settings.videoEnabled === true,
  };
}

/** Republicar mic se o usuário queria estar aberto e o Krisp mexeu no pipeline. */
export function shouldRecoverMicrophoneAfterKrisp(input: {
  krispPipelineChanged: boolean;
  microphoneIntendedOn: boolean;
}): boolean {
  return input.krispPipelineChanged && input.microphoneIntendedOn;
}

type AudioContextLike = {
  state: string;
  resume: () => Promise<void>;
};

/**
 * Usa o clique de “Entrar” (gesto) para acordar o AudioContext do browser.
 * O Room ainda não existe no Prejoin — startAudio() vem depois, no connect.
 */
export async function unlockBrowserAudioPlayback(opts?: {
  createContext?: () => AudioContextLike | null;
}): Promise<boolean> {
  try {
    const create =
      opts?.createContext ??
      (() => {
        if (typeof window === 'undefined') return null;
        const Ctor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return null;
        return new Ctor() as unknown as AudioContextLike;
      });
    const ctx = create();
    if (!ctx) return false;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    return ctx.state === 'running';
  } catch {
    return false;
  }
}

export async function startRoomAudioIfBlocked(room: {
  canPlaybackAudio: boolean;
  startAudio: () => Promise<unknown>;
}): Promise<boolean> {
  if (!shouldAttemptStartAudio(room.canPlaybackAudio)) {
    return true;
  }
  try {
    await room.startAudio();
    return true;
  } catch {
    return false;
  }
}
